import React, { ReactElement, Component, createRef, RefObject } from 'react'
import styled from 'styled-components'
import Grid from '../Grid'
import FlatListProps, { ViewToken } from './FlatListProps'
import ListVisibilitySensor from './ListVisibilitySensor/index.web'

type ScrollToOffsetProps = {
    offset: number
    animated?: boolean
}

type State = {
    containment?: HTMLDivElement | null
}

export const ScrollingWrapper = styled(Grid)`
    ${(props): string => `flex-wrap: ${props.horizontal ? 'nowrap' : ''};
    overflow-x: ${props.horizontal ? 'auto' : 'hidden'};
    overflow-y: ${props.horizontal ? 'hidden' : 'auto'};
    -webkit-overflow-scrolling: touch;
    ${
        props.horizontal
            ? `&::-webkit-scrollbar {
                display: none;
            } /* Chrome */
            -ms-overflow-style: none;  /* Internet Explorer 10+ */
            scrollbar-width: none;  /* Firefox */`
            : ''
    }`}
`

export const ScrollingItem = styled(Grid)`
    flex: 0 0 auto;
`

class FlatList<ItemT> extends Component<FlatListProps<ItemT>, State> {
    flatListRef: RefObject<HTMLDivElement | null> = createRef()
    viewableItems: ViewToken<ItemT>[] = []

    state: State = {}

    scrollToOffset = ({ offset, animated = true }: ScrollToOffsetProps): void => {
        const { horizontal } = this.props
        if (this.flatListRef.current?.scrollTo) {
            this.flatListRef.current?.scrollTo({
                top: horizontal ? undefined : offset,
                left: horizontal ? offset : undefined,
                behavior: animated ? 'smooth' : undefined,
            })
        }
    }

    onViewableItemsChanged = (changed: ViewToken<ItemT>): void => {
        const { onViewableItemsChanged } = this.props
        if (changed.isViewable) {
            const changedIndex = this.viewableItems.findIndex(item => item.key === changed.key)
            if (changedIndex === -1) {
                this.viewableItems.push(changed)
            } else {
                this.viewableItems[changedIndex] = changed
            }
        } else {
            this.viewableItems = this.viewableItems.filter(item => item.key !== changed.key)
        }
        onViewableItemsChanged &&
            onViewableItemsChanged({ viewableItems: this.viewableItems, changed: [changed] })
    }

    componentDidMount(): void {
        const { innerRef, onScroll } = this.props
        if (onScroll) {
            this.flatListRef.current?.addEventListener('scroll', () => {
                onScroll({
                    scrollTop: this.flatListRef.current?.scrollTop,
                    scrollLeft: this.flatListRef.current?.scrollLeft,
                })
            })
        }

        if (this.flatListRef?.current) {
            this.setState({ containment: this.flatListRef.current })
            if (innerRef) {
                innerRef.current = {
                    scrollToOffset: (params): void => {
                        this.scrollToOffset(params)
                    },
                }
            }
        }
    }

    public render(): ReactElement | null {
        const {
            data,
            renderItem,
            keyExtractor,
            horizontal = false,
            ItemSeparatorComponent,
            ListEmptyComponent,
            ListFooterComponent,
            ListHeaderComponent,
            testID,
            viewabilityConfig,
        } = this.props

        return (
            <ScrollingWrapper
                noExpand
                testID={testID}
                horizontal={horizontal}
                ref={this.flatListRef}
            >
                {ListHeaderComponent ? (
                    <ScrollingItem>{ListHeaderComponent}</ScrollingItem>
                ) : undefined}
                {data.length >= 1
                    ? data.map(
                          (item, index): ReactElement => (
                              <ListVisibilitySensor
                                  key={keyExtractor(item, index)}
                                  horizontal={horizontal}
                                  index={index}
                                  item={item}
                                  keyExtractor={keyExtractor}
                                  onViewableItemsChanged={this.onViewableItemsChanged}
                                  containment={this.state.containment}
                                  itemVisiblePercentThreshold={
                                      viewabilityConfig?.itemVisiblePercentThreshold
                                  }
                              >
                                  <ScrollingItem testID={`flatlist-item-wrapper_${index}`}>
                                      {renderItem({ item, index })}
                                  </ScrollingItem>
                                  {index < data.length - 1 && ItemSeparatorComponent ? (
                                      <ScrollingItem testID={`flatlist-seperator-wrapper_${index}`}>
                                          {ItemSeparatorComponent({})}
                                      </ScrollingItem>
                                  ) : null}
                              </ListVisibilitySensor>
                          )
                      )
                    : ListEmptyComponent}
                {ListFooterComponent ? (
                    <ScrollingItem>{ListFooterComponent}</ScrollingItem>
                ) : undefined}
            </ScrollingWrapper>
        )
    }
}
export default FlatList
